import {
  CommandId,
  MessageId,
  ORCHESTRATION_WS_METHODS,
  ProjectId,
  ThreadId,
  type ClientOrchestrationCommand,
} from "@afar-research/contracts";
import * as Crypto from "effect/Crypto";
import * as Clock from "effect/Clock";
import * as DateTime from "effect/DateTime";
import * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";

import { clientReadiness } from "../connection/model.ts";
import { EnvironmentSupervisor } from "../connection/supervisor.ts";
import {
  type EnvironmentRpcFailure,
  type EnvironmentRpcSuccess,
  EnvironmentRpcUnavailableError,
  request,
} from "../rpc/client.ts";

type CommandType = ClientOrchestrationCommand["type"];
type CommandOf<T extends CommandType> = Extract<ClientOrchestrationCommand, { readonly type: T }>;
type CommandInput<T extends CommandType> = Omit<
  CommandOf<T>,
  "type" | "commandId" | "createdAt"
> & {
  readonly commandId?: CommandId;
} & ("createdAt" extends keyof CommandOf<T>
    ? {
        readonly createdAt?: CommandOf<T>["createdAt"];
      }
    : {});

export type CreateProjectInput = CommandInput<"project.create">;
type ProjectFromReferencesCommandInput = CommandInput<"project.create-from-references">;
export interface ProjectCreationIdentities {
  readonly commandId: CommandId;
  readonly projectId: ProjectId;
  readonly threadId: ThreadId;
  readonly messageId: MessageId;
}
export type CreateProjectFromReferencesInput = Omit<
  ProjectFromReferencesCommandInput,
  keyof ProjectCreationIdentities
> &
  ProjectCreationIdentities;
export type UpdateProjectInput = CommandInput<"project.meta.update">;
export type StartProjectStudyInput = CommandInput<"project.study.start"> & {
  readonly commandId: CommandId;
};
export type SkipProjectStudyInput = CommandInput<"project.study.skip">;
export type DeleteProjectInput = CommandInput<"project.delete">;
export type CreateThreadInput = CommandInput<"thread.create">;
export type ForkThreadInput = CommandInput<"thread.fork">;
export type DeleteThreadInput = CommandInput<"thread.delete">;
export type ManageThreadInput = CommandInput<"thread.management.set">;
export type PinThreadInput = CommandInput<"thread.pin">;
export type UnpinThreadInput = CommandInput<"thread.unpin">;
export type ReorderPinnedThreadInput = CommandInput<"thread.pin.reorder">;
export type ReorderActiveThreadInput = CommandInput<"thread.active.reorder">;
export type ArchiveThreadInput = CommandInput<"thread.archive">;
export type UnarchiveThreadInput = CommandInput<"thread.unarchive">;
export type LinkThreadPullRequestInput = CommandInput<"thread.pull-request.link">;
export type UnlinkThreadPullRequestInput = CommandInput<"thread.pull-request.unlink">;
export type UpdateThreadMetadataInput = CommandInput<"thread.meta.update">;
export type SetThreadRuntimeModeInput = CommandInput<"thread.runtime-mode.set">;
export type SetThreadInteractionModeInput = CommandInput<"thread.interaction-mode.set">;
export type StartThreadTurnInput = CommandInput<"thread.turn.start">;
export type ControlGoalInput = CommandInput<"thread.goal">;
export type CompactThreadInput = CommandInput<"thread.compact">;
export type InterruptThreadTurnInput = CommandInput<"thread.turn.interrupt">;
export type RespondToThreadApprovalInput = CommandInput<"thread.approval.respond">;
export type RespondToThreadUserInputInput = CommandInput<"thread.user-input.respond">;
export type RevertThreadCheckpointInput = CommandInput<"thread.checkpoint.revert"> & {
  /** Omitted restores the workspace; `false` rewinds history only. */
  readonly restoreFiles?: boolean;
};
export type StopThreadSessionInput = CommandInput<"thread.session.stop">;
export type WarmThreadSessionInput = CommandInput<"thread.session.warm">;

type DispatchTag = typeof ORCHESTRATION_WS_METHODS.dispatchCommand;
type CommandEffect = Effect.Effect<
  EnvironmentRpcSuccess<DispatchTag>,
  EnvironmentRpcFailure<DispatchTag> | EnvironmentRpcUnavailableError,
  Crypto.Crypto | EnvironmentSupervisor
>;
const ENVIRONMENT_COMMAND_MAX_ATTEMPTS = 5;
const ENVIRONMENT_COMMAND_DEADLINE_MS = 30_000;
const ENVIRONMENT_COMMAND_INITIAL_BACKOFF_MS = 250;
const ENVIRONMENT_COMMAND_MAX_BACKOFF_MS = 5_000;

function commandId(input: { readonly commandId?: CommandId }) {
  return Effect.gen(function* () {
    if (input.commandId !== undefined) {
      return input.commandId;
    }
    const crypto = yield* Crypto.Crypto;
    return yield* crypto.randomUUIDv4.pipe(Effect.orDie, Effect.map(CommandId.make));
  });
}

function timestampedCommandMetadata(input: {
  readonly commandId?: CommandId;
  readonly createdAt?: string;
}) {
  return Effect.all({
    commandId: commandId(input),
    createdAt:
      input.createdAt === undefined
        ? DateTime.now.pipe(Effect.map(DateTime.formatIso))
        : Effect.succeed(input.createdAt),
  });
}

function dispatch(command: ClientOrchestrationCommand) {
  return request(ORCHESTRATION_WS_METHODS.dispatchCommand, command);
}

function isUncertainDispatchFailure(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("_tag" in error)) return false;
  const tag = (error as { readonly _tag?: unknown })._tag;
  if (tag !== "RpcClientError" || !("reason" in error)) return false;
  const reason = error.reason;
  return (
    typeof reason === "object" &&
    reason !== null &&
    "_tag" in reason &&
    typeof reason._tag === "string" &&
    reason._tag.startsWith("Socket")
  );
}

function boundedBackoffMs(commandId: string, completedAttempts: number): number {
  const base = Math.min(
    ENVIRONMENT_COMMAND_INITIAL_BACKOFF_MS * 2 ** Math.max(0, completedAttempts - 1),
    ENVIRONMENT_COMMAND_MAX_BACKOFF_MS,
  );
  const seed = Array.from(commandId).reduce(
    (total, character) => total + character.charCodeAt(0),
    0,
  );
  const jitter = 0.8 + ((seed + completedAttempts * 17) % 41) / 100;
  return Math.min(ENVIRONMENT_COMMAND_MAX_BACKOFF_MS, Math.round(base * jitter));
}

const dispatchReceiptCommand = Effect.fn("EnvironmentCommands.dispatchReceiptCommand")(function* (
  command: ClientOrchestrationCommand,
) {
  const supervisor = yield* EnvironmentSupervisor;
  const initial = yield* SubscriptionRef.get(supervisor.state);
  if (clientReadiness(initial) !== "ready") {
    return yield* new EnvironmentRpcUnavailableError({
      environmentId: supervisor.target.environmentId,
      message: `${supervisor.target.label} is not ready; the mutation was not retained.`,
    });
  }

  const startedAt = yield* Clock.currentTimeMillis;
  let lastAmbiguousFailure:
    | EnvironmentRpcFailure<DispatchTag>
    | EnvironmentRpcUnavailableError
    | null = null;
  for (let attempt = 1; attempt <= ENVIRONMENT_COMMAND_MAX_ATTEMPTS; attempt += 1) {
    const observedGeneration = (yield* SubscriptionRef.get(supervisor.state)).generation;
    const elapsed = (yield* Clock.currentTimeMillis) - startedAt;
    const remaining = ENVIRONMENT_COMMAND_DEADLINE_MS - elapsed;
    if (remaining <= 0) {
      return yield* (
        lastAmbiguousFailure ??
          new EnvironmentRpcUnavailableError({
            environmentId: supervisor.target.environmentId,
            message: "Environment Command dispatch reached its 30-second deadline.",
          })
      );
    }

    const attemptResult = yield* dispatch(command).pipe(
      Effect.timeoutOption(Duration.millis(remaining)),
      Effect.flatMap(
        Option.match({
          onNone: () =>
            Effect.fail(
              new EnvironmentRpcUnavailableError({
                environmentId: supervisor.target.environmentId,
                message: "Environment Command dispatch reached its 30-second deadline.",
              }),
            ),
          onSome: Effect.succeed,
        }),
      ),
      Effect.result,
    );
    if (attemptResult._tag === "Success") {
      return attemptResult.success;
    }
    if (!isUncertainDispatchFailure(attemptResult.failure)) {
      return yield* Effect.fail(attemptResult.failure);
    }
    lastAmbiguousFailure = attemptResult.failure;
    if (attempt === ENVIRONMENT_COMMAND_MAX_ATTEMPTS) {
      return yield* Effect.fail(attemptResult.failure);
    }

    const remainingBeforeBackoff =
      ENVIRONMENT_COMMAND_DEADLINE_MS - ((yield* Clock.currentTimeMillis) - startedAt);
    if (remainingBeforeBackoff <= 0) {
      return yield* Effect.fail(attemptResult.failure);
    }
    const backoffMs = Math.min(
      boundedBackoffMs(command.commandId, attempt),
      remainingBeforeBackoff,
    );
    yield* Effect.logWarning("ambiguous Environment Command dispatch; retrying exact request", {
      commandId: command.commandId,
      commandKind: command.type,
      attempt,
      maxAttempts: ENVIRONMENT_COMMAND_MAX_ATTEMPTS,
      backoffMs,
    });
    yield* Effect.sleep(Duration.millis(backoffMs));
    const elapsedAfterBackoff = (yield* Clock.currentTimeMillis) - startedAt;
    const reconnectBudget = ENVIRONMENT_COMMAND_DEADLINE_MS - elapsedAfterBackoff;
    if (reconnectBudget <= 0) {
      return yield* Effect.fail(attemptResult.failure);
    }
    const current = yield* SubscriptionRef.get(supervisor.state);
    if (current.generation <= observedGeneration || clientReadiness(current) !== "ready") {
      const reconnected = yield* SubscriptionRef.changes(supervisor.state).pipe(
        Stream.filter(
          (state) => state.generation > observedGeneration && clientReadiness(state) === "ready",
        ),
        Stream.runHead,
        Effect.timeoutOption(Duration.millis(reconnectBudget)),
      );
      if (Option.isNone(reconnected) || Option.isNone(reconnected.value)) {
        return yield* Effect.fail(attemptResult.failure);
      }
    }
  }
  return yield* new EnvironmentRpcUnavailableError({
    environmentId: supervisor.target.environmentId,
    message: "Environment Command dispatch exhausted its retry budget.",
  });
});

export function makeProjectCreationIdentities(makeId: () => string): ProjectCreationIdentities {
  return {
    commandId: CommandId.make(makeId()),
    projectId: ProjectId.make(makeId()),
    threadId: ThreadId.make(makeId()),
    messageId: MessageId.make(makeId()),
  };
}

export const createProjectFromReferences: (
  input: CreateProjectFromReferencesInput,
) => CommandEffect = Effect.fn("EnvironmentCommands.createProjectFromReferences")(
  function* (input) {
    const createdAt = input.createdAt ?? (yield* DateTime.now.pipe(Effect.map(DateTime.formatIso)));
    return yield* dispatchReceiptCommand({
      ...input,
      type: "project.create-from-references",
      commandId: input.commandId,
      createdAt,
    });
  },
);

/** Start a project's Project Study; the identities come from the dialog so a retry repeats it. */
export const startProjectStudy: (input: StartProjectStudyInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.startProjectStudy",
)(function* (input) {
  const createdAt = input.createdAt ?? (yield* DateTime.now.pipe(Effect.map(DateTime.formatIso)));
  return yield* dispatchReceiptCommand({ ...input, type: "project.study.start", createdAt });
});

export const skipProjectStudy: (input: SkipProjectStudyInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.skipProjectStudy",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatchReceiptCommand({
    ...input,
    type: "project.study.skip",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});

export const createProject: (input: CreateProjectInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.createProject",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatchReceiptCommand({
    ...input,
    type: "project.create",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});

export const updateProject: (input: UpdateProjectInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.updateProject",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "project.meta.update",
    commandId: yield* commandId(input),
  });
});

export const deleteProject: (input: DeleteProjectInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.deleteProject",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "project.delete",
    commandId: yield* commandId(input),
  });
});

export const createThread: (input: CreateThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.createThread",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.create",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});

export const forkThread: (input: ForkThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.forkThread",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.fork",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});

export const deleteThread: (input: DeleteThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.deleteThread",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.delete",
    commandId: yield* commandId(input),
  });
});

export const archiveThread: (input: ArchiveThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.archiveThread",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.archive",
    commandId: yield* commandId(input),
  });
});

/**
 * Link a pull request to a thread. A client may only ever link as `manual`: the Environment
 * assigns every other source, so stack membership cannot be claimed from here.
 */
export const linkThreadPullRequest: (input: LinkThreadPullRequestInput) => CommandEffect =
  Effect.fn("EnvironmentCommands.linkThreadPullRequest")(function* (input) {
    return yield* dispatchReceiptCommand({
      ...input,
      type: "thread.pull-request.link",
      commandId: yield* commandId(input),
    });
  });

/**
 * Remove a pull request from a thread. A member of a known native stack is dismissed rather
 * than removed, so a later discovery does not put it back; the Environment decides which.
 */
export const unlinkThreadPullRequest: (input: UnlinkThreadPullRequestInput) => CommandEffect =
  Effect.fn("EnvironmentCommands.unlinkThreadPullRequest")(function* (input) {
    return yield* dispatchReceiptCommand({
      ...input,
      type: "thread.pull-request.unlink",
      commandId: yield* commandId(input),
    });
  });

export const manageThread: (input: ManageThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.manageThread",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.management.set",
    commandId: yield* commandId(input),
  });
});

export const pinThread: (input: PinThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.pinThread",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.pin",
    commandId: yield* commandId(input),
  });
});

export const unpinThread: (input: UnpinThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.unpinThread",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.unpin",
    commandId: yield* commandId(input),
  });
});

export const reorderPinnedThread: (input: ReorderPinnedThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.reorderPinnedThread",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.pin.reorder",
    commandId: yield* commandId(input),
  });
});

export const reorderActiveThread: (input: ReorderActiveThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.reorderActiveThread",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.active.reorder",
    commandId: yield* commandId(input),
  });
});

export const unarchiveThread: (input: UnarchiveThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.unarchiveThread",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.unarchive",
    commandId: yield* commandId(input),
  });
});

export const updateThreadMetadata: (input: UpdateThreadMetadataInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.updateThreadMetadata",
)(function* (input) {
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.meta.update",
    commandId: yield* commandId(input),
  });
});

export const setThreadRuntimeMode: (input: SetThreadRuntimeModeInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.setThreadRuntimeMode",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.runtime-mode.set",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});

export const setThreadInteractionMode: (input: SetThreadInteractionModeInput) => CommandEffect =
  Effect.fn("EnvironmentCommands.setThreadInteractionMode")(function* (input) {
    const metadata = yield* timestampedCommandMetadata(input);
    return yield* dispatchReceiptCommand({
      ...input,
      type: "thread.interaction-mode.set",
      commandId: metadata.commandId,
      createdAt: metadata.createdAt,
    });
  });

export const startThreadTurn: (input: StartThreadTurnInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.startThreadTurn",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.turn.start",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});

export const controlGoal: (input: ControlGoalInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.controlGoal",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.goal",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});

export const compactThread: (input: CompactThreadInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.compactThread",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.compact",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});

export const interruptThreadTurn: (input: InterruptThreadTurnInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.interruptThreadTurn",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.turn.interrupt",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});

export const respondToThreadApproval: (input: RespondToThreadApprovalInput) => CommandEffect =
  Effect.fn("EnvironmentCommands.respondToThreadApproval")(function* (input) {
    const metadata = yield* timestampedCommandMetadata(input);
    return yield* dispatchReceiptCommand({
      ...input,
      type: "thread.approval.respond",
      commandId: metadata.commandId,
      createdAt: metadata.createdAt,
    });
  });

export const respondToThreadUserInput: (input: RespondToThreadUserInputInput) => CommandEffect =
  Effect.fn("EnvironmentCommands.respondToThreadUserInput")(function* (input) {
    const metadata = yield* timestampedCommandMetadata({
      ...input,
      commandId:
        input.commandId ?? CommandId.make(`thread-user-input-response:${input.expectedRevision}`),
    });
    return yield* dispatchReceiptCommand({
      ...input,
      type: "thread.user-input.respond",
      commandId: metadata.commandId,
      createdAt: metadata.createdAt,
    });
  });

export const revertThreadCheckpoint: (input: RevertThreadCheckpointInput) => CommandEffect =
  Effect.fn("EnvironmentCommands.revertThreadCheckpoint")(function* (input) {
    const metadata = yield* timestampedCommandMetadata(input);
    const { restoreFiles, ...command } = input;
    return yield* dispatchReceiptCommand({
      ...command,
      // Two commands rather than one flag: an older Environment Service must
      // reject a history-only rewind outright rather than ignore the option
      // and restore files the user asked to keep.
      type: restoreFiles === false ? "thread.conversation.revert" : "thread.checkpoint.revert",
      commandId: metadata.commandId,
      createdAt: metadata.createdAt,
    });
  });

/**
 * Ask the Environment to bring a thread's provider session up before a prompt
 * needs it. Fire and forget: the result is not awaited anywhere on a send path,
 * and a failure is left for the turn itself to report.
 */
export const warmThreadSession: (input: WarmThreadSessionInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.warmThreadSession",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatch({
    ...input,
    type: "thread.session.warm",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});

export const stopThreadSession: (input: StopThreadSessionInput) => CommandEffect = Effect.fn(
  "EnvironmentCommands.stopThreadSession",
)(function* (input) {
  const metadata = yield* timestampedCommandMetadata(input);
  return yield* dispatchReceiptCommand({
    ...input,
    type: "thread.session.stop",
    commandId: metadata.commandId,
    createdAt: metadata.createdAt,
  });
});
